import { DataService } from '../../../../services/data_service';
import { Formulare } from './Formulare';
import { IDs } from '../../../../helpers/IDs';
import { i18n } from '../../../../i18n/i18n';
import { Md5 } from 'ts-md5/dist/md5';
import { StructureService } from '../../../../services/structure_service';
import { TriggerService } from '../../../../core/trigger/trigger_service';

export class Translation {

    private curOptionparts: any[] = [];
    private data: any = {};
    private dataGlobal: any = {};
    private DataService: DataService;
    private static entities: any = {};
    private static structures: any = {};
    private TriggerService: TriggerService;

    constructor(data: any, dataService: DataService, triggerService: TriggerService, resetEntities?: boolean) {
        this.dataGlobal = data || {};
        this.DataService = dataService;
        this.TriggerService = triggerService;
        this.init(resetEntities);
    }

    public getData(): any {
        return this.data;
    }

    public async getEntities(): Promise<any[]> {
        return new Promise((resolve: any): void => {
            const entities: any[] = [];
            this.loadEntities(() => {
                Formulare.entities.forEach((entity: any) => {
                    const fields: any[] = [];
                    const structure: any = this.getStructure(entity.id, entity.module);

                    (structure.ATTS || []).forEach((item: any) => {
                        if (item.NAME && +item.HIDDEN !== 1 && (item.HIDDEN || '').toString().toLowerCase() !== 'yes') {
                            const key: string = item.KEY || item.ID || null;
                            const options: any[] = this.getOptions(item);
                            const optionparts: any[] = this.curOptionparts;
                            fields.push({
                                index: fields.length, ...(key && { key } ), name: item.NAME, ...(!!options.length && { options }),
                                ...(!!optionparts.length && { optionparts })
                            });
                        }
                    });
                    entities.push({ ...entity, name: structure.TITEL, counter: fields.length, fields });
                });
                resolve(entities);
            });
        });
    }

    public getHash(data: string): string {
        return typeof data === 'string' && data.length ? Md5.hashAsciiStr(data).toString() : null;
    }

    public getLocales(): any {
        const locales: any = {};
        const structure: any = this.getStructure(IDs.ENT_MEHRSPRACHIGKEIT, IDs.MOD_ETOOL_ADMIN);
        const atts: any[] = structure.ATTS || [];

        atts.forEach((item: any) => {
            if (item.TYPE !== 'CHECKBOX') {
                return;
            }
            const key: string = (item.KEY || '').toString().split('_')[0];
            const locale: any = atts.find((item: any) => item.KEY === key && item.NAME);

            if (locale && locale.KEY !== 'de') {
                locales[locale.KEY] = i18n(locale.NAME, 'sys');
            }
        });

        return locales;
    }

    public getTranslation(data: string, locale: string, callback?: (data: any) => void): any {
        const intern_key: string = this.getHash(data);
        const translation: string = (this.data[intern_key] || {})[locale];
        const isTranslated: boolean = typeof translation === 'string' && !!translation.length;
        const result: string = isTranslated ? translation : data;

        if (typeof callback === 'function') {
            callback({
                isTranslated,
                isVerified: !!(this.data[intern_key] || {})[`${locale}_geprueft`],
                text: result
            });
        }

        return result;
    }

    public setData(data: any, locale: string, callback?: () => void): void {
        data = data || {};
        const isVerified: number = typeof data.isVerified === 'boolean' ? +!!data.isVerified : null;
        const intern_key: string = typeof data.name === 'string' && data.name.length ? data.name : null;
        const text: string = typeof data.text === 'string' ? data.text : null;

        if (!intern_key || (isVerified === null && text === null)) {
            return;
        }
        const tid: number = ((this.dataGlobal[IDs.ENT_MEHRSPRACHIGKEIT] || [])
            .find((item: any) => item.intern_key === intern_key && item.intern_key2 === 'entity') || {}).tid || 0;
        const item: any = {
            tid,
            intern_key,
            intern_key2: 'entity',
            ...(text !== null && { [locale]: text }),
            ...(isVerified !== null && { [`${locale}_geprueft`]: isVerified })
        };
        this.DataService.setData(IDs.MOD_ETOOL_ADMIN, IDs.ENT_MEHRSPRACHIGKEIT, item, (insertID: number) => {
            this.TriggerService.fire('admin_reload', IDs.ENT_MEHRSPRACHIGKEIT);

            if (typeof callback === 'function') {
                callback();
            }
        });
    }

    protected getEntityData(entity: string, filter: any, settings: any, module: string, callback: (data: any) => void): void {
        this.DataService.loadData(
            entity, filter, { ...settings, ...{ resolve: true, bundle: true } },
            (data: any, entity: string) => {
                if (typeof callback === 'function') {
                    callback((data || {})[entity] || []);
                }
            },
            module
        );
    }

    protected getOptions(data: any): any {
        const options: Array<any[]> = [];
        let groups: Array<any[]> = Array.isArray(data.OPTS) ? [data.OPTS] : [];
        this.curOptionparts = [];

        if (!Array.isArray(data.OPTS) && Object.keys(data.OPTS || {}).length) {
            groups = this.getRefOptions(data.OPTS);
        }

        groups.forEach((items: any[], index: number) => {
            options.push([]);

            items.forEach((item: any) => {
                const key: string = typeof item.VALUE === 'number' || typeof item.VALUE === 'string' ? item.VALUE.toString() : null;
                const name: string = item.TITEL;
                options[index].push({ ...(key !== null && { key }), name });
            });
        });

        return options;
    }

    protected getRefOptions(opts: any): any[] {
        const data: Array<any[]> = [];
        const groups: Array<any[]> = [];
        opts = opts || {};

        if (!opts.ENTITY || opts.MODUL !== IDs.MOD_ETOOL_ADMIN || !opts.INPUT) {
            return([]);
        }

        if (opts.OUTPUT) {
            const dataEntity: any[] = Translation.entities[opts.ENTITY] || [];
            const structure: any = this.getStructure(opts.ENTITY, opts.MODUL);
            const atts: string[] = (structure.ATTS || []).map((att: any) => att.KEY);

            (opts.OUTPUT || []).forEach((value: string) => {
                if (typeof value !== 'string') {
                    return;
                }

                if (atts.indexOf(value) !== -1) {
                    const items: any[] = [];

                    dataEntity.forEach((item: any) => {
                        if (item[value] !== null && typeof item[value] !== 'undefined' && item[value].trim().length) {
                            items.push({ tid: item.tid, name: item[value] });
                        }
                    });
                    groups.push(items);
                    this.curOptionparts.push({ name: value });
                } else if (value !== '\n') {
                    const name: string = value.startsWith('B:') ? value.substring(2) : value;
                    groups.push([{ tid: 1, name }]);
                    this.curOptionparts.push({ name });
                }
            });
        } else {
            groups.push(Translation.entities[opts.ENTITY] || []);
        }

        groups.forEach((items: any[]) => {
            data.push(items.map((item: any) => { return { VALUE: item.tid, TITEL: item[opts.INPUT] || item['name'] } }));
        });

        return data;
    }

    protected getStructure(entity: string, module: string): any {
        if (Translation.structures[`${entity}@${module}`]) {
            return Translation.structures[`${entity}@${module}`];
        }

        return Translation.structures[`${entity}@${module}`] = (((StructureService || {}).structures || {})[module] || {})[entity] || {};
    }

    protected init(resetEntities?: boolean): void {
        if (resetEntities) {
            Translation.entities = {};
        }

        (this.dataGlobal[IDs.ENT_MEHRSPRACHIGKEIT] || []).forEach((item: any) => {
            const intern_key: string = this.getHash(item.intern_key);

            if (intern_key !== null && item.intern_key2 === 'entity' && !this.data[intern_key]) {
                this.data[intern_key] = item;
            }
        });
    }

    protected loadEntities(callback: () => void): void {
        const entities: any = {};
        const update = (entity: string): void => {
            delete entities[entity];

            if (!Object.keys(entities).length && typeof callback === 'function') {
                callback();
            }
        };

        if (Object.keys(Translation.entities).length) {
            update(null);
            return;
        }

        Formulare.entities.forEach((entity: any) => {
            const structure: any = this.getStructure(entity.id, entity.module);
            const atts: any[] = structure.ATTS || [];

            atts.forEach((item: any) => {
                let opts: any = item.OPTS;

                if (
                    item.NAME && +item.HIDDEN !== 1 && (item.HIDDEN || '').toString().toLowerCase() !== 'yes'! && !Array.isArray(opts)
                    && Object.keys(opts || {}).length
                ) {
                    if (!opts.ENTITY || Translation.entities[opts.ENTITY] || opts.MODUL !== IDs.MOD_ETOOL_ADMIN || !opts.INPUT) {
                        return;
                    }
                    entities[opts.ENTITY] = null;
                }
            });
        });

        Object.keys(entities).forEach((entity: string) => {
            if (this.dataGlobal[entity]) {
                Translation.entities[entity] = this.dataGlobal[entity];
                update(entity);
                return;
            }
            this.getEntityData(entity, null, null, IDs.MOD_ETOOL_ADMIN, (data: any[]) => {
                Translation.entities[entity] = data;
                update(entity);
            });
        });
    }
}
